/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { Field, Control, Label, Input } from "rbx";

import moment from "moment";
import { startOfDay, endOfDay } from "date-fns";

import { useQuery } from "react-apollo";

import gql from "graphql-tag";

import { compose } from "../../../../utils";

import { ALL_HOLIDAYS_QUERY } from "../../../../graphql/Holidays/Holidays.graphql";

const getStyles = isSpecial =>
  isSpecial
    ? {
        color: "red",
        fontWeight: "bolder"
      }
    : {};

const SINGLE_ROUTE_QUERY = gql`
  query SINGLE_ROUTE_QUERY($where: RouteWhereUniqueInput!) {
    Route(where: $where) {
      RecId
      StartDate
      EndDate
    }
  }
`;

const SINGLE_TARIFF_QUERY = gql`
  query SINGLE_TARIFF_QUERY($where: TariffWhereUniqueInput!) {
    Tariff(where: $where) {
      RecId
      BeginDate
      EndDate
    }
  }
`;

export const withOutsideRouteDateRange = WrappedComponent => props => {
  const { routeRecId, value } = props;

  const [isOutsideRange, setIsOutsideRange] = useState(false);

  const { data: RouteData } = useQuery(SINGLE_ROUTE_QUERY, {
    variables: {
      where: {
        RecId: routeRecId || null
      }
    }
  });
  useEffect(() => {
    if (RouteData && RouteData.Route) {
      const { StartDate, EndDate } = RouteData.Route;
      const routeStartDate = startOfDay(new Date(StartDate));
      const routeEndDate = endOfDay(new Date(EndDate));
      const deliveryDate = new Date(value);
      setIsOutsideRange(
        deliveryDate < routeStartDate || deliveryDate > routeEndDate
      );
    }
  }, [RouteData, value]);
  return (
    <WrappedComponent
      {...props}
      isOutsideRange={isOutsideRange}
      isOutsideRouteRange={isOutsideRange}
    />
  );
};

export const withOutsideTariffDateRange = WrappedComponent => props => {
  const { tariffRecId, value } = props;

  const [isOutsideRange, setIsOutsideRange] = useState(false);

  const { data: TariffData } = useQuery(SINGLE_TARIFF_QUERY, {
    variables: {
      where: {
        RecId: tariffRecId
      }
    }
  });
  useEffect(() => {
    if (TariffData && TariffData.Tariff) {
      const { BeginDate, EndDate } = TariffData.Tariff;
      const tariffBeginDate = moment(BeginDate)
        .startOf("day")
        .toDate();
      const tariffEndDate = moment(EndDate)
        .endOf("day")
        .toDate();
      const deliveryDate = new Date(value);
      setIsOutsideRange(
        deliveryDate < tariffBeginDate || deliveryDate > tariffEndDate
      );
    }
  }, [TariffData, tariffRecId, value]);

  return (
    <WrappedComponent
      {...props}
      isOutsideRange={isOutsideRange}
      isOutsideTariffRange={isOutsideRange}
    />
  );
};

export const withOutsideDateRange = compose(
  withOutsideTariffDateRange,
  withOutsideRouteDateRange
);

export const OutsideTariffDateRangeDisplay = withOutsideTariffDateRange(
  ({
    name,
    label,
    isOutsideRange = false,
    dateFormat = '"dddd, MMM D, YYYY"',
    displayDate
  }) => {
    const [displayValue, setDisplayValue] = useState("");

    useEffect(() => {
      const date = moment(displayDate);
      const dv = date.format(dateFormat);

      setDisplayValue(dv);
    }, [dateFormat, displayDate]);

    return (
      <Field>
        <Control>
          <Label>{label}</Label>
          <Input
            readOnly
            name={name}
            style={getStyles(isOutsideRange)}
            type="text"
            value={displayValue}
          />
        </Control>
      </Field>
    );
  }
);

OutsideTariffDateRangeDisplay.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  isOutsideRange: PropTypes.bool,
  dateFormat: PropTypes.string,
  displayDate: PropTypes.string
};

export const OutsideRouteDateRangeDisplay = withOutsideRouteDateRange(
  ({
    name,
    label,
    isOutsideRange = false,
    dateFormat = '"dddd, MMM D, YYYY"',
    displayDate
  }) => {
    const [displayValue, setDisplayValue] = useState("");

    useEffect(() => {
      const date = moment(displayDate);
      const dv = date.format(dateFormat);

      setDisplayValue(dv);
    }, [dateFormat, displayDate]);

    return (
      <Field>
        <Control>
          <Label>{label}</Label>
          <Input
            readOnly
            name={name}
            style={getStyles(isOutsideRange)}
            type="text"
            value={displayValue}
          />
        </Control>
      </Field>
    );
  }
);

OutsideRouteDateRangeDisplay.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  isOutsideRange: PropTypes.bool,
  dateFormat: PropTypes.string,
  displayDate: PropTypes.string
};

const DeliveryDate = ({
  name,
  value = new Date().toISOString(),
  isOutsideRange = false
}) => {
  const { data: HolidayData } = useQuery(ALL_HOLIDAYS_QUERY);

  const [displayValue, setDisplayValue] = useState("");
  const [isSaturday, setIsSaturday] = useState(false);
  const [isSunday, setIsSunday] = useState(false);
  const [isHoliday, setIsHoliday] = useState(false);

  useEffect(() => {
    const date = value ? moment(value) : moment();
    const dv = date.format("MM/DD/YYYY (ddd)");

    setDisplayValue(dv);

    const weekday = date.weekday();
    setIsSaturday(weekday === 6);
    setIsSunday(weekday === 0);
  }, [value]);

  useEffect(() => {
    if (HolidayData && Array.isArray(HolidayData.Holidays)) {
      setIsHoliday(
        HolidayData.Holidays.some(holiday =>
          moment(holiday.Date).isSame(value, "day")
        )
      );
    }
  }, [HolidayData, value]);

  return (
    <Field>
      <Control>
        <Label>Delivery Date</Label>
        <Input
          readOnly
          name={name}
          style={getStyles(
            isSaturday || isSunday || isHoliday || isOutsideRange
          )}
          type="text"
          value={displayValue}
        />
      </Control>
    </Field>
  );
};

DeliveryDate.propTypes = {
  name: PropTypes.string,
  value: PropTypes.string,
  isOutsideRange: PropTypes.bool
};

export default withOutsideDateRange(DeliveryDate);
